import { Client } from '@stomp/stompjs';
import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_CONNECTION_ATTEMPTS = 3;

const useWebSocket = (url, shouldConnect, protocol13) => {
  const [clientSocket, setClientSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [connectionFailed, setConnectionFailed] = useState(false);
  const clientRef = useRef(null);
  const failedAttemptsRef = useRef(0);

  const createClient = useCallback(() => {
    const client = new Client();
    client.configure({
      brokerURL: url,
      webSocketFactory: () => {
        // For QA and dev-stage environments, use the following protocol configuration:
        // const protocols = protocol13
        //   ? ['v13.stomp', 'v12.stomp', 'v11.stomp', 'v10.stomp', 'stomp']
        //   : ['v12.stomp', 'v11.stomp', 'v10.stomp', 'stomp'];
        // console.log('Creating WebSocket with protocols:', protocols);
        // return new WebSocket(url, protocols);

        const protocols = ['v13.stomp'];
        console.log('Creating WebSocket with protocols:', protocols);
        return new WebSocket(url, protocols);
      },

      forceBinaryWSFrames: true,
      appendMissingNULLonIncoming: true,
      debug: (str) => {
        console.log(`Debug WebSocket: ${str}`);
      },
      onConnect: (frame) => {
        console.log('WebSocket connection established successfully', frame?.headers);
        clientRef.current = client;
        failedAttemptsRef.current = 0;
        setClientSocket(client);
        setIsConnected(true);
        setConnectionAttempts(0);
      },
      onDisconnect: (frame) => {
        console.log('WebSocket disconnection:', frame?.headers, frame?.body);

        setIsConnected(false);
      },
      onWebSocketClose: (event) => {
        console.log('WebSocket closed:', { code: event.code, reason: event.reason, wasClean: event.wasClean });
        setIsConnected(false);
        if (!event.wasClean) {
          failedAttemptsRef.current += 1;
          if (failedAttemptsRef.current >= MAX_CONNECTION_ATTEMPTS) {
            console.error(`Max WebSocket connection attempts (${MAX_CONNECTION_ATTEMPTS}) reached, stopping reconnects`);
            client.reconnectDelay = 0;
            client.deactivate();
            setConnectionFailed(true);
          }
        }
      },
      onWebSocketError: (error) => {
        const errorMessage = {
          message: error.message,
          type: error.type,
          code: error.code,
          timestamp: new Date().toISOString(),
        };
        console.error('WebSocket error:', errorMessage);
        setIsConnected(false);
      },
      onStompError: (frame) => {
        const stompError = {
          message: frame.headers?.message || 'Unknown STOMP error',
          detail: frame.body,
          timestamp: new Date().toISOString(),
        };
        console.error('STOMP error:', stompError);
        setIsConnected(false);
        failedAttemptsRef.current += 1;
        if (failedAttemptsRef.current >= MAX_CONNECTION_ATTEMPTS) {
          console.error(`Max WebSocket connection attempts (${MAX_CONNECTION_ATTEMPTS}) reached, stopping reconnects`);
          client.reconnectDelay = 0;
          client.deactivate();
          setConnectionFailed(true);
        }
      },
    
      onUnhandledMessage: (message) => {
        console.warn('Unhandled STOMP message received:', {
          destination: message.headers?.destination,
          subscriptionId: message.headers?.subscription,
          messageId: message.headers?.['message-id'],
          bodyPreview: message.body?.substring(0, 200),
        });
      },
   
      onUnhandledFrame: (frame) => {
        console.warn('Unhandled STOMP frame:', frame.command, frame.headers);
      },

      onUnhandledReceipt: (frame) => {
        console.log('Unhandled receipt:', frame.headers);
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,

      connectionTimeout: 10000,

    });

    return client;
  }, [url]);

  const reconnect = useCallback(() => {
    if (connectionFailed) {
      console.error('Connection has failed permanently, cannot reconnect');
      return;
    }
    if (connectionAttempts < MAX_CONNECTION_ATTEMPTS) {
      console.log(`Attempting to reconnect... (attempt ${connectionAttempts + 1})`);
      setConnectionAttempts(prev => prev + 1);

      if (clientSocket) {
        clientSocket.deactivate();
      }

      setTimeout(() => {
        const newClient = createClient();
        newClient.activate();
      }, 1000 * (connectionAttempts + 1));
    } else {
      console.error('Max reconnection attempts reached');
      setConnectionFailed(true);
    }
  }, [clientSocket, connectionAttempts, connectionFailed, createClient]);

  useEffect(() => {
    if (!shouldConnect) {
      if (clientSocket) {
        clientSocket.deactivate();
        setClientSocket(null);
        setIsConnected(false);
      }
      return;
    }

    failedAttemptsRef.current = 0;
    setConnectionFailed(false);
    const client = createClient();
    client.activate();

    return () => {
      client.deactivate();
      setClientSocket(null);
      setIsConnected(false);
    };
  }, [url, shouldConnect, createClient]);

  return {
    client: clientSocket,
    isConnected,
    reconnect,
    connectionAttempts,
    connectionFailed,
  };
};

export default useWebSocket;
